// How a pre-visit brief that was just filed in the AI Chat (POST clinic/visit-brief/chat/)
// becomes messages in the chat's state, without a reload of the history.
//
// The chat keeps its conversation as flat messages: each exchange is a user message and an
// assistant message that share one `feedbackId` (the ai_feedback row) and one `pinned` flag.
//
// The same answer can also say which exchanges the server took OUT of the chat (`removed_ids`):
// the chat thread is keyed by the visit, and when the front desk re-points the visit at another
// patient the brief filed for the old one is deleted. Those exchanges leave the conversation (and
// with it the pinned bar) here, and a history read that began before the delete must not bring
// them back (keepAttachedBriefs).

// The prompt the backend files a brief under (previsit_brief_chat.PROMPT). The chat's pinned bar
// shows the first line of a prompt.
export const BRIEF_PROMPT = "📋 Pre-visit brief";

// The feedbackIds of the brief exchanges in `messages`. A message loaded from the history
// carries no `kind`, so a brief is told by its prompt, which the backend writes as BRIEF_PROMPT.
function briefExchangeIds(messages) {
  return new Set(
    messages
      .filter((m) => m.role === "user" && m.text === BRIEF_PROMPT && m.feedbackId != null)
      .map((m) => m.feedbackId),
  );
}

function unpin(messages, ids) {
  if (ids.size === 0) return messages;
  return messages.map((m) => (m.pinned && ids.has(m.feedbackId) ? { ...m, pinned: false } : m));
}

// `messages` without the exchanges whose feedbackId is in `ids`; the same array when none is.
// A message with no feedbackId yet (the greeting, one just typed) is never matched.
function without(messages, ids) {
  if (!ids || ids.size === 0) return messages;
  const kept = messages.filter((m) => m.feedbackId == null || !ids.has(m.feedbackId));
  return kept.length === messages.length ? messages : kept;
}

function timeOf(iso) {
  let when = iso ? new Date(iso) : new Date();
  if (Number.isNaN(when.getTime())) when = new Date();
  return when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// The ids of the exchanges the server removed from the chat, from its answer. Blank ids are
// left out: a null would match every message that has no feedbackId yet.
export function removedBriefIds(result) {
  const ids = result && result.removed_ids;
  return Array.isArray(ids) ? ids.filter((id) => id != null && id !== "") : [];
}

// Notes the server's answer in `ledger`, the Set of exchange ids it has removed from this chat so
// far: the ones it removed go in, and the one it filed (which can be an id it removed earlier: the
// row's id is worked out from the brief, so the same brief comes back under the same id if the
// visit is pointed back at its patient) comes out.
export function noteRemovedBriefs(ledger, result) {
  removedBriefIds(result).forEach((id) => ledger.add(id));
  if (result && result.feedback_id != null) ledger.delete(result.feedback_id);
}

// `messages` after the server's answer: without the exchanges it removed, and with the brief it
// filed appended as a pinned exchange, every earlier brief unpinned (the server did the same to
// its rows: only the newest brief stays pinned). The brief is also appended when the server says
// the thread ALREADY holds it (`attached: false`, with the exchange): the first answer can be lost
// on the way, and the retry is what brings the brief on screen. The messages are returned as they
// were when there is nothing to change: no removal, and no brief, or one already in the conversation.
export function withAttachedBrief(messages, result, makeLocalId) {
  const base = without(messages, new Set(removedBriefIds(result)));
  if (!result || result.feedback_id == null || !result.response) return base;
  if (base.some((m) => m.feedbackId === result.feedback_id)) return base;
  const shared = {
    time: timeOf(result.created_at),
    feedbackId: result.feedback_id,
    pinned: true,
    status: null,
    // Marks a message the chat added itself, so a history load that started before the
    // server had the row (keepAttachedBriefs) does not wipe it.
    briefAttach: true,
  };
  return [
    ...unpin(base, briefExchangeIds(base)),
    { role: "user", text: result.prompt || BRIEF_PROMPT, localId: makeLocalId(), ...shared },
    { role: "assistant", text: result.response, localId: makeLocalId(), ...shared },
  ];
}

// The history `loaded` from the server, plus any brief attached to the conversation `current`
// while that load was in flight which the server's read did not include, and minus the exchanges
// in `removedIds` (a Set: what the server's answers have removed so far). The chat replaces its
// messages with the loaded history, the brief is filed by a request that can finish first, and a
// read that the server served before it deleted a stale brief still holds that brief.
// Returns `loaded` itself when there is nothing to keep or drop.
export function keepAttachedBriefs(loaded, current, removedIds) {
  const visible = without(loaded, removedIds);
  const have = new Set(visible.map((m) => m.feedbackId));
  const kept = current.filter((m) => m.briefAttach && !have.has(m.feedbackId));
  if (kept.length === 0) return visible;
  return [...unpin(visible, briefExchangeIds(visible)), ...kept];
}
